import React, { useState, createContext, useContext } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { 
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
  SheetFooter,
} from '@/components/ui/sheet';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { 
  ShoppingBag, 
  Package, 
  Gift, 
  Star, 
  ChevronRight,
  Plus,
  Minus,
  Check,
  Truck,
  Heart,
  Sparkles,
  X,
  ShoppingCart,
  Trash2,
  CreditCard,
  ArrowRight
} from 'lucide-react';
import { toast } from 'sonner';

// Cart Context
const CartContext = createContext();

const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within CartProvider');
  }
  return context;
};

const CartProvider = ({ children }) => {
  const [items, setItems] = useState([]);
  const [isOpen, setIsOpen] = useState(false);

  const addItem = (product, size, price) => {
    const itemKey = `${product.id}-${size}`;
    setItems(prev => {
      const existing = prev.find(item => item.key === itemKey);
      if (existing) {
        return prev.map(item => 
          item.key === itemKey 
            ? { ...item, quantity: item.quantity + 1 }
            : item
        );
      }
      return [...prev, {
        key: itemKey,
        id: product.id,
        name: product.name,
        image: product.image,
        size,
        price,
        quantity: 1
      }];
    });
    setIsOpen(true);
    toast.success(`${product.name} (${size}) added to cart!`);
  };

  const removeItem = (key) => {
    setItems(prev => prev.filter(item => item.key !== key));
  };

  const updateQuantity = (key, delta) => {
    setItems(prev => prev.map(item => {
      if (item.key === key) {
        const newQty = item.quantity + delta;
        if (newQty < 1) return item;
        return { ...item, quantity: newQty };
      }
      return item;
    }));
  };

  const clearCart = () => {
    setItems([]);
  };

  const itemCount = items.reduce((sum, item) => sum + item.quantity, 0);
  const subtotal = items.reduce((sum, item) => sum + (item.price * item.quantity), 0);
  const shipping = subtotal >= 50 ? 0 : 7.99;
  const total = subtotal + shipping;

  return (
    <CartContext.Provider value={{
      items,
      addItem,
      removeItem,
      updateQuantity,
      clearCart,
      itemCount,
      subtotal,
      shipping,
      total,
      isOpen,
      setIsOpen
    }}>
      {children}
    </CartContext.Provider>
  );
};

// Product data
const products = {
  fudge: [
    { id: 'f1', name: 'Classic Chocolate', description: 'Rich, creamy traditional chocolate fudge', image: 'https://images.unsplash.com/photo-1548907040-4baa42d10919?w=400&h=300&fit=crop', popular: true, category: 'Fudge' },
    { id: 'f2', name: 'Peanut Butter Swirl', description: 'Chocolate and peanut butter perfection', image: 'https://images.unsplash.com/photo-1606312619070-d48b4c652a52?w=400&h=300&fit=crop', popular: true, category: 'Fudge' },
    { id: 'f3', name: 'Maple Walnut', description: 'Vermont maple with crunchy walnuts', image: 'https://images.unsplash.com/photo-1599599810769-bcde5a160d32?w=400&h=300&fit=crop', category: 'Fudge' },
    { id: 'f4', name: 'Sea Salt Caramel', description: 'Sweet caramel with a hint of sea salt', image: 'https://images.unsplash.com/photo-1582176604856-e824b4736522?w=400&h=300&fit=crop', popular: true, category: 'Fudge' },
    { id: 'f5', name: 'Rocky Road', description: 'Marshmallows and almonds in chocolate', image: 'https://images.unsplash.com/photo-1553452118-621e1f860f43?w=400&h=300&fit=crop', category: 'Fudge' },
    { id: 'f6', name: 'White Chocolate Raspberry', description: 'Creamy white chocolate with raspberry swirl', image: 'https://images.unsplash.com/photo-1571115177098-24ec42ed204d?w=400&h=300&fit=crop', category: 'Fudge' },
  ],
  nuts: [
    { id: 'n1', name: 'Honey Roasted Cashews', description: 'Sweet and crunchy perfection', image: 'https://images.unsplash.com/photo-1536816579748-4ecb3f03d72a?w=400&h=300&fit=crop', popular: true, category: 'Nuts' },
    { id: 'n2', name: 'Butter Toffee Almonds', description: 'Buttery toffee coating on premium almonds', image: 'https://images.unsplash.com/photo-1608797178974-15b35a64ede9?w=400&h=300&fit=crop', category: 'Nuts' },
    { id: 'n3', name: 'Cinnamon Glazed Pecans', description: 'Southern-style cinnamon sugar pecans', image: 'https://images.unsplash.com/photo-1597371424128-8ffb9082c828?w=400&h=300&fit=crop', popular: true, category: 'Nuts' },
    { id: 'n4', name: 'Mixed Nut Brittle', description: 'Crunchy brittle with assorted nuts', image: 'https://images.unsplash.com/photo-1599599810694-b5b37304c041?w=400&h=300&fit=crop', category: 'Nuts' },
  ],
  candy: [
    { id: 'c1', name: 'Saltwater Taffy Assortment', description: 'Classic flavors in every pull', image: 'https://images.unsplash.com/photo-1581798459219-318e76aecc7b?w=400&h=300&fit=crop', popular: true, category: 'Candy' },
    { id: 'c2', name: 'Chocolate Truffles', description: 'Hand-rolled gourmet truffles', image: 'https://images.unsplash.com/photo-1549007994-cb92caebd54b?w=400&h=300&fit=crop', popular: true, category: 'Candy' },
    { id: 'c3', name: 'Caramel Corn', description: 'Buttery caramel popcorn goodness', image: 'https://images.unsplash.com/photo-1578849278619-e73505e9610f?w=400&h=300&fit=crop', category: 'Candy' },
    { id: 'c4', name: 'Fruit Jellies', description: 'Natural fruit flavored jellies', image: 'https://images.unsplash.com/photo-1582058091505-f87a2e55a40f?w=400&h=300&fit=crop', category: 'Candy' },
  ]
};

const sizes = [
  { weight: '1/4 lb', price: 8.99 },
  { weight: '1/2 lb', price: 15.99 },
  { weight: '1 lb', price: 28.99 },
];

const bundles = [
  { 
    id: 'b1', 
    name: 'Sweet Sampler', 
    description: '1/4 lb each of 4 varieties of your choice',
    price: 29.99,
    savings: 6,
    items: '4 × 1/4 lb'
  },
  { 
    id: 'b2', 
    name: 'Family Favorites', 
    description: '1/2 lb each of 3 varieties of your choice',
    price: 42.99,
    savings: 5,
    items: '3 × 1/2 lb'
  },
  { 
    id: 'b3', 
    name: 'Gift Box Deluxe', 
    description: '1 lb each of 2 varieties in a gift box',
    price: 52.99,
    savings: 5,
    items: '2 × 1 lb + Gift Box'
  },
  { 
    id: 'b4', 
    name: 'Party Pack', 
    description: 'Mix & match 3 lbs total - perfect for events',
    price: 74.99,
    savings: 12,
    items: '3 lbs total'
  },
];

// Cart Drawer Component
const CartDrawer = () => {
  const { items, removeItem, updateQuantity, itemCount, subtotal, shipping, total, isOpen, setIsOpen, clearCart } = useCart();
  const [checkoutOpen, setCheckoutOpen] = useState(false);

  return (
    <>
      <Sheet open={isOpen} onOpenChange={setIsOpen}>
        <SheetTrigger asChild>
          <Button className="bg-white text-amber-900 hover:bg-amber-100 relative">
            <ShoppingBag className="w-5 h-5 mr-2" />
            Cart
            {itemCount > 0 && (
              <span className="absolute -top-2 -right-2 bg-amber-600 text-white text-xs w-5 h-5 rounded-full flex items-center justify-center">
                {itemCount}
              </span>
            )}
          </Button>
        </SheetTrigger>
        <SheetContent className="w-full sm:max-w-lg flex flex-col">
          <SheetHeader>
            <SheetTitle className="flex items-center gap-2">
              <ShoppingCart className="w-5 h-5" />
              Your Cart ({itemCount} items)
            </SheetTitle>
          </SheetHeader>
          
          {items.length === 0 ? (
            <div className="flex-1 flex flex-col items-center justify-center text-center p-6">
              <ShoppingBag className="w-16 h-16 text-slate-300 mb-4" />
              <h3 className="text-lg font-semibold text-slate-900">Your cart is empty</h3>
              <p className="text-slate-500 mt-2">Add some sweet treats to get started!</p>
              <Button 
                className="mt-6 bg-amber-600 hover:bg-amber-700"
                onClick={() => setIsOpen(false)}
              >
                Continue Shopping
              </Button>
            </div>
          ) : (
            <>
              <div className="flex-1 overflow-auto py-4 space-y-4">
                {items.map((item) => (
                  <div key={item.key} className="flex gap-4 p-3 bg-slate-50 rounded-lg">
                    <img 
                      src={item.image} 
                      alt={item.name}
                      className="w-20 h-20 object-cover rounded-lg"
                    />
                    <div className="flex-1">
                      <div className="flex justify-between">
                        <div>
                          <h4 className="font-semibold text-slate-900">{item.name}</h4>
                          <p className="text-sm text-slate-500">{item.size}</p>
                        </div>
                        <button 
                          onClick={() => removeItem(item.key)}
                          className="text-slate-400 hover:text-red-500 transition-colors"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                      <div className="flex items-center justify-between mt-2">
                        <div className="flex items-center gap-2">
                          <button 
                            onClick={() => updateQuantity(item.key, -1)}
                            className="w-7 h-7 rounded-full bg-slate-200 hover:bg-slate-300 flex items-center justify-center transition-colors"
                            disabled={item.quantity <= 1}
                          >
                            <Minus className="w-3 h-3" />
                          </button>
                          <span className="w-8 text-center font-medium">{item.quantity}</span>
                          <button 
                            onClick={() => updateQuantity(item.key, 1)}
                            className="w-7 h-7 rounded-full bg-slate-200 hover:bg-slate-300 flex items-center justify-center transition-colors"
                          >
                            <Plus className="w-3 h-3" />
                          </button>
                        </div>
                        <span className="font-semibold text-amber-700">
                          ${(item.price * item.quantity).toFixed(2)}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Cart Summary */}
              <div className="border-t pt-4 space-y-3">
                <div className="flex justify-between text-slate-600">
                  <span>Subtotal</span>
                  <span>${subtotal.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-slate-600">
                  <span>Shipping</span>
                  {shipping === 0 ? (
                    <span className="text-green-600 font-medium">FREE</span>
                  ) : (
                    <span>${shipping.toFixed(2)}</span>
                  )}
                </div>
                {subtotal < 50 && (
                  <p className="text-sm text-amber-600 bg-amber-50 p-2 rounded">
                    Add ${(50 - subtotal).toFixed(2)} more for FREE shipping!
                  </p>
                )}
                <div className="flex justify-between text-lg font-bold text-slate-900 pt-2 border-t">
                  <span>Total</span>
                  <span>${total.toFixed(2)}</span>
                </div>
                
                <Button 
                  className="w-full bg-amber-600 hover:bg-amber-700 text-lg py-6"
                  onClick={() => {
                    setIsOpen(false);
                    setCheckoutOpen(true);
                  }}
                >
                  <CreditCard className="w-5 h-5 mr-2" />
                  Proceed to Checkout
                </Button>
                
                <Button 
                  variant="ghost" 
                  className="w-full text-slate-500"
                  onClick={() => setIsOpen(false)}
                >
                  Continue Shopping
                </Button>
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>

      {/* Checkout Dialog */}
      <Dialog open={checkoutOpen} onOpenChange={setCheckoutOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CreditCard className="w-5 h-5 text-amber-600" />
              Checkout
            </DialogTitle>
          </DialogHeader>
          <div className="py-6 text-center">
            <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Sparkles className="w-8 h-8 text-amber-600" />
            </div>
            <h3 className="text-xl font-bold text-slate-900 mb-2">Ready to Connect Shopify!</h3>
            <p className="text-slate-600 mb-6">
              This is where Shopify checkout would appear. Once you have a Shopify account, 
              I can connect it to handle:
            </p>
            <ul className="text-left space-y-2 mb-6 bg-slate-50 p-4 rounded-lg">
              <li className="flex items-center gap-2 text-slate-700">
                <Check className="w-4 h-4 text-green-600" /> Secure payment processing
              </li>
              <li className="flex items-center gap-2 text-slate-700">
                <Check className="w-4 h-4 text-green-600" /> Shipping calculations
              </li>
              <li className="flex items-center gap-2 text-slate-700">
                <Check className="w-4 h-4 text-green-600" /> Order management
              </li>
              <li className="flex items-center gap-2 text-slate-700">
                <Check className="w-4 h-4 text-green-600" /> Inventory tracking
              </li>
              <li className="flex items-center gap-2 text-slate-700">
                <Check className="w-4 h-4 text-green-600" /> Email confirmations
              </li>
            </ul>
            <div className="space-y-3">
              <p className="text-sm text-slate-500">
                Your cart total: <strong className="text-amber-700">${total.toFixed(2)}</strong>
              </p>
              <Button 
                className="w-full bg-amber-600 hover:bg-amber-700"
                onClick={() => {
                  toast.success("Demo checkout complete! In production, this would process payment via Shopify.");
                  clearCart();
                  setCheckoutOpen(false);
                }}
              >
                Complete Demo Order
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

// Product Card with Add to Cart
const ProductCard = ({ product }) => {
  const { addItem } = useCart();
  const [selectedSize, setSelectedSize] = useState(null);
  const [showSizeSelector, setShowSizeSelector] = useState(false);

  const handleAddToCart = (size) => {
    const sizeData = sizes.find(s => s.weight === size);
    addItem(product, size, sizeData.price);
    setShowSizeSelector(false);
    setSelectedSize(null);
  };

  return (
    <Card className="group overflow-hidden border-0 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
      <div className="relative aspect-[4/3] overflow-hidden">
        <img 
          src={product.image} 
          alt={product.name}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
        />
        {product.popular && (
          <Badge className="absolute top-3 left-3 bg-amber-500 text-white">
            <Star className="w-3 h-3 mr-1 fill-current" /> Popular
          </Badge>
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
        
        {/* Quick Add Button */}
        <button 
          className="absolute bottom-3 right-3 w-10 h-10 bg-white rounded-full shadow-lg flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-300 hover:bg-amber-500 hover:text-white"
          onClick={() => setShowSizeSelector(true)}
        >
          <Plus className="w-5 h-5" />
        </button>
      </div>
      <CardContent className="p-4">
        <p className="text-xs text-amber-600 font-medium mb-1">{product.category}</p>
        <h3 className="font-bold text-lg text-slate-900">{product.name}</h3>
        <p className="text-sm text-slate-500 mt-1">{product.description}</p>
        
        {showSizeSelector ? (
          <div className="mt-4 space-y-2">
            <p className="text-sm font-medium text-slate-700">Select size:</p>
            <div className="grid grid-cols-3 gap-2">
              {sizes.map((size) => (
                <button
                  key={size.weight}
                  onClick={() => handleAddToCart(size.weight)}
                  className="p-2 text-center border-2 border-amber-200 rounded-lg hover:border-amber-500 hover:bg-amber-50 transition-all"
                >
                  <span className="block text-sm font-bold text-slate-900">{size.weight}</span>
                  <span className="block text-xs text-amber-600">${size.price}</span>
                </button>
              ))}
            </div>
            <button 
              onClick={() => setShowSizeSelector(false)}
              className="text-sm text-slate-500 hover:text-slate-700"
            >
              Cancel
            </button>
          </div>
        ) : (
          <div className="flex items-center justify-between mt-4">
            <span className="text-sm text-slate-600">From <strong className="text-amber-700">${sizes[0].price}</strong></span>
            <Button 
              size="sm"
              className="bg-amber-600 hover:bg-amber-700"
              onClick={() => setShowSizeSelector(true)}
            >
              <Plus className="w-4 h-4 mr-1" />
              Add
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// Bundle Card with Add to Cart
const BundleCard = ({ bundle }) => {
  const { addItem } = useCart();

  const handleAddBundle = () => {
    addItem(
      { id: bundle.id, name: bundle.name, image: 'https://images.unsplash.com/photo-1549007994-cb92caebd54b?w=400&h=300&fit=crop' },
      bundle.items,
      bundle.price
    );
  };

  return (
    <Card className="border-2 border-amber-200 hover:border-amber-400 transition-colors overflow-hidden group">
      <div className="bg-gradient-to-br from-amber-500 to-amber-600 p-4 text-white">
        <div className="flex justify-between items-start">
          <Gift className="w-8 h-8" />
          <Badge className="bg-white text-amber-700">Save ${bundle.savings}</Badge>
        </div>
        <h3 className="text-xl font-bold mt-3">{bundle.name}</h3>
        <p className="text-amber-100 text-sm mt-1">{bundle.items}</p>
      </div>
      <CardContent className="p-4">
        <p className="text-slate-600 text-sm mb-4">{bundle.description}</p>
        <div className="flex items-center justify-between">
          <span className="text-2xl font-bold text-amber-700">${bundle.price}</span>
          <Button 
            className="bg-amber-600 hover:bg-amber-700"
            onClick={handleAddBundle}
          >
            <Plus className="w-4 h-4 mr-1" />
            Add Bundle
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

// Main Component
const SweetShopWithCart = () => {
  return (
    <CartProvider>
      <div className="min-h-screen bg-amber-50">
        {/* Header */}
        <header className="bg-gradient-to-r from-amber-900 via-amber-800 to-amber-900 text-white sticky top-0 z-50">
          <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center">
                <span className="text-2xl">🍫</span>
              </div>
              <div>
                <h1 className="text-2xl font-bold tracking-tight">Sweet Carolina Confections</h1>
                <p className="text-amber-200 text-sm">Handcrafted in Southport, NC</p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <Button variant="ghost" className="text-white hover:bg-amber-700 hidden md:flex">
                <Heart className="w-5 h-5 mr-2" />
                Favorites
              </Button>
              <CartDrawer />
            </div>
          </div>
        </header>

        {/* Hero Section */}
        <section className="relative bg-gradient-to-br from-amber-800 via-amber-700 to-amber-900 text-white overflow-hidden">
          <div className="absolute inset-0 opacity-20">
            <div className="absolute top-20 left-20 w-40 h-40 bg-amber-400 rounded-full blur-3xl" />
            <div className="absolute bottom-20 right-20 w-60 h-60 bg-orange-400 rounded-full blur-3xl" />
          </div>
          <div className="max-w-7xl mx-auto px-6 py-20 relative">
            <div className="grid md:grid-cols-2 gap-12 items-center">
              <div>
                <Badge className="bg-amber-500/20 text-amber-200 border-amber-400/30 mb-6">
                  <Sparkles className="w-4 h-4 mr-2" />
                  Now Shipping Nationwide
                </Badge>
                <h2 className="text-4xl md:text-5xl font-bold leading-tight mb-6">
                  Handcrafted <br />
                  <span className="text-amber-300">Sweet Treats</span>
                </h2>
                <p className="text-xl text-amber-100 mb-8 leading-relaxed">
                  From our kitchen to your door. Premium fudge, roasted nuts, and 
                  artisan candies made in small batches with love.
                </p>
                <div className="flex items-center gap-8 text-amber-200">
                  <div className="flex items-center gap-2">
                    <Check className="w-5 h-5 text-amber-400" />
                    <span>Free Shipping $50+</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Check className="w-5 h-5 text-amber-400" />
                    <span>Made Fresh Daily</span>
                  </div>
                </div>
              </div>
              <div className="relative hidden md:block">
                <div className="grid grid-cols-2 gap-4">
                  <img 
                    src="https://images.unsplash.com/photo-1548907040-4baa42d10919?w=300&h=300&fit=crop" 
                    alt="Chocolate Fudge"
                    className="w-full rounded-2xl shadow-2xl transform rotate-3 hover:rotate-0 transition-transform"
                  />
                  <img 
                    src="https://images.unsplash.com/photo-1549007994-cb92caebd54b?w=300&h=300&fit=crop" 
                    alt="Truffles"
                    className="w-full rounded-2xl shadow-2xl transform -rotate-3 hover:rotate-0 transition-transform mt-8"
                  />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Size Options Banner */}
        <section className="bg-white py-8 border-b">
          <div className="max-w-7xl mx-auto px-6">
            <div className="flex flex-wrap items-center justify-center gap-8 md:gap-16">
              <div className="text-center">
                <Package className="w-8 h-8 text-amber-600 mx-auto mb-2" />
                <p className="font-bold text-slate-900">1/4 Pound</p>
                <p className="text-sm text-amber-600 font-semibold">$8.99</p>
              </div>
              <div className="text-center">
                <Package className="w-10 h-10 text-amber-600 mx-auto mb-2" />
                <p className="font-bold text-slate-900">1/2 Pound</p>
                <p className="text-sm text-amber-600 font-semibold">$15.99</p>
              </div>
              <div className="text-center">
                <Package className="w-12 h-12 text-amber-600 mx-auto mb-2" />
                <p className="font-bold text-slate-900">1 Pound</p>
                <p className="text-sm text-amber-600 font-semibold">$28.99</p>
              </div>
              <div className="text-center border-l pl-8 md:pl-16">
                <Gift className="w-10 h-10 text-amber-600 mx-auto mb-2" />
                <p className="font-bold text-slate-900">Bundles</p>
                <p className="text-sm text-green-600 font-semibold">Save up to $12!</p>
              </div>
            </div>
          </div>
        </section>

        {/* Bundles Section */}
        <section className="py-16 bg-gradient-to-b from-amber-100 to-white">
          <div className="max-w-7xl mx-auto px-6">
            <div className="text-center mb-12">
              <Badge className="bg-amber-200 text-amber-800 mb-4">Best Value</Badge>
              <h2 className="text-4xl font-bold text-slate-900 mb-4">Bundle & Save</h2>
              <p className="text-xl text-slate-600 max-w-2xl mx-auto">
                Mix and match your favorites. Perfect for gifts or stocking up!
              </p>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {bundles.map((bundle) => (
                <BundleCard key={bundle.id} bundle={bundle} />
              ))}
            </div>
          </div>
        </section>

        {/* Fudge Section */}
        <section className="py-16 bg-white">
          <div className="max-w-7xl mx-auto px-6">
            <div className="flex items-center justify-between mb-10">
              <div>
                <h2 className="text-3xl font-bold text-slate-900">Handmade Fudge</h2>
                <p className="text-slate-600 mt-2">Made fresh in small batches daily</p>
              </div>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {products.fudge.map((product) => (
                <ProductCard key={product.id} product={product} />
              ))}
            </div>
          </div>
        </section>

        {/* Nuts Section */}
        <section className="py-16 bg-amber-50">
          <div className="max-w-7xl mx-auto px-6">
            <div className="flex items-center justify-between mb-10">
              <div>
                <h2 className="text-3xl font-bold text-slate-900">Roasted Nuts</h2>
                <p className="text-slate-600 mt-2">Premium nuts, perfectly seasoned</p>
              </div>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {products.nuts.map((product) => (
                <ProductCard key={product.id} product={product} />
              ))}
            </div>
          </div>
        </section>

        {/* Candy Section */}
        <section className="py-16 bg-white">
          <div className="max-w-7xl mx-auto px-6">
            <div className="flex items-center justify-between mb-10">
              <div>
                <h2 className="text-3xl font-bold text-slate-900">Artisan Candy</h2>
                <p className="text-slate-600 mt-2">Classic favorites & gourmet treats</p>
              </div>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {products.candy.map((product) => (
                <ProductCard key={product.id} product={product} />
              ))}
            </div>
          </div>
        </section>

        {/* Shipping Info */}
        <section className="py-16 bg-gradient-to-r from-amber-800 to-amber-900 text-white">
          <div className="max-w-7xl mx-auto px-6">
            <div className="grid md:grid-cols-3 gap-8 text-center">
              <div>
                <Truck className="w-12 h-12 mx-auto mb-4 text-amber-300" />
                <h3 className="text-xl font-bold mb-2">Free Shipping</h3>
                <p className="text-amber-200">On orders over $50</p>
              </div>
              <div>
                <Package className="w-12 h-12 mx-auto mb-4 text-amber-300" />
                <h3 className="text-xl font-bold mb-2">Gift Ready</h3>
                <p className="text-amber-200">Beautiful packaging included</p>
              </div>
              <div>
                <Heart className="w-12 h-12 mx-auto mb-4 text-amber-300" />
                <h3 className="text-xl font-bold mb-2">Made with Love</h3>
                <p className="text-amber-200">Family recipes since 1985</p>
              </div>
            </div>
          </div>
        </section>

        {/* Footer */}
        <footer className="bg-amber-950 text-amber-200 py-8">
          <div className="max-w-7xl mx-auto px-6 text-center">
            <p className="text-sm">© 2026 Sweet Carolina Confections. Demo Store.</p>
            <p className="mt-2 text-amber-400">🍬 Handcrafted in Southport, NC 🍬</p>
          </div>
        </footer>
      </div>
    </CartProvider>
  );
};

export default SweetShopWithCart;
